import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Checkbox,
  IconButton,
} from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import {
  getProviderServices,
  sendQuotationToProviderLead,
} from "../../../api/provider-section/provider-service-management";
import { AlertComponent } from "../../../common/components";
import "./send-quotation-modal.scss";

interface SubService {
  id: number;
  name: string;
  description?: string;
  price: string;
}

interface Merchandise {
  id: number;
  name: string;
  description?: string;
  price: string;
}

interface ProviderService {
  id: number;
  categoryName: string;
  serviceName: string;
  serviceDescription?: string;
  subServices: SubService[];
  merchandises: Merchandise[];
}

interface SendQuotationModalProps {
  open: boolean;
  onClose: () => void;
  partnerId: number;
  leadId: number;
  leadName: string;
  leadEmail: string;
  onSuccess?: () => void;
}

const SendQuotationModal: React.FC<SendQuotationModalProps> = ({
  open,
  onClose,
  partnerId,
  leadId,
  leadName,
  leadEmail,
  onSuccess,
}) => {
  const [services, setServices] = useState<ProviderService[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<number>>(
    new Set()
  );
  const [subServicesExpanded, setSubServicesExpanded] = useState<Set<number>>(
    () => new Set()
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("info");

  useEffect(() => {
    if (open && partnerId) {
      fetchServices();
      setSelectedServiceIds(new Set());
      setSubServicesExpanded(new Set());
    }
  }, [open, partnerId]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await getProviderServices(partnerId);
      const servicesData = response?.data?.data?.services;
      if (Array.isArray(servicesData)) {
        setServices(servicesData);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error("Error fetching provider services:", error);
      setAlertMessage("Failed to fetch services. Please try again.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubServices = (serviceId: number) => {
    setSubServicesExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleServiceToggle = (serviceId: number) => {
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleSendQuotation = async () => {
    if (selectedServiceIds.size === 0) {
      setAlertMessage("Please select at least one service to send quotation.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setSending(true);
    try {
      const partnerServiceIds = Array.from(selectedServiceIds);
      const response = await sendQuotationToProviderLead(leadId, {
        partnerServiceIds,
      });

      if (response?.data?.success) {
        setAlertMessage(
          response?.data?.data?.message ||
          "Quotation has been sent successfully to the lead."
        );
        setAlertType("success");
        setAlertOpen(true);
        setSelectedServiceIds(new Set());
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        throw new Error(
          response?.data?.message || "Failed to send quotation"
        );
      }
    } catch (error: any) {
      console.error("Error sending quotation:", error);
      setAlertMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send quotation. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setSending(false);
    }
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(numPrice)
      ? "$0.00"
      : `$${numPrice.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
  };

  return (
    <>
      <Modal open={open} onClose={onClose} className="send-quotation-modal1">
        <Box className="send-quotation-modal1__content">
          <Box className="send-quotation-modal1__header">
            <Typography variant="h5" className="send-quotation-modal1__title1">
              Send Quotation
            </Typography>
            <Typography
              variant="body2"
              className="send-quotation-modal1__subtitle1"
            >
              This quotation will be sent to the lead via email
            </Typography>
          </Box>

          <Box className="send-quotation-modal1__body">
            <Box className="send-quotation-modal1__recipient">
              <Typography
                variant="body2"
                className="send-quotation-modal1__recipient-label1"
              >
                Sending to:
              </Typography>
              <Typography
                component="span"
                variant="body1"
                className="send-quotation-modal1__recipient-info"
              >
                <span className="send-quotation-modal1__recipient-name1">{leadName}</span>
                {" - "}
                <span className="send-quotation-modal1__recipient-email1">{leadEmail}</span>
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={4}
              >
                <CircularProgress size={40} />
              </Box>
            ) : services.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography color="textSecondary">
                  No services available
                </Typography>
              </Box>
            ) : (
              <>
                {services.map((service, index) => (
                  <Box
                    key={service.id}
                    className="send-quotation-modal1__package-section"
                  >
                    {index > 0 && <Divider sx={{ my: 3 }} />}

                    <Box className="send-quotation-modal1__package-header">
                      <Checkbox
                        size="small"
                        checked={selectedServiceIds.has(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                        className="send-quotation-modal1__package-checkbox1"
                      />
                      <Box className="send-quotation-modal1__package-name-section">
                        <Typography
                          variant="body2"
                          className="send-quotation-modal1__package-name-label1"
                        >
                          {service.categoryName}
                        </Typography>
                        <Box className="send-quotation-modal1__package-name-wrapper">
                          <Typography
                            variant="body1"
                            className="send-quotation-modal1__package-name-value1"
                          >
                            {service.serviceName}
                          </Typography>
                        </Box>
                        {service.serviceDescription && (
                          <Typography
                            variant="body2"
                            className="send-quotation-modal1__service-description1"
                          >
                            {service.serviceDescription}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box className="send-quotation-modal1__categories">
                      {service.subServices?.length > 0 && (
                        <Box className="send-quotation-modal1__category-item">
                          <Box
                            className="send-quotation-modal1__category-title-row send-quotation-modal1__category-title-row--clickable"
                            onClick={() => toggleSubServices(service.id)}
                          >
                            <Box className="send-quotation-modal1__category-title-group">
                              <IconButton
                                size="small"
                                className="send-quotation-modal1__accordion-icon"
                                aria-label={subServicesExpanded.has(service.id) ? "Collapse" : "Expand"}
                                sx={{ p: 0 }}
                              >
                                {subServicesExpanded.has(service.id) ? (
                                  <ExpandMore />
                                ) : (
                                  <ChevronRight />
                                )}
                              </IconButton>
                              <Typography
                                variant="subtitle2"
                                className="send-quotation-modal1__category-title1"
                              >
                                Sub Services
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              className="send-quotation-modal1__category-item-count1"
                            >
                              {service.subServices.length} item{service.subServices.length !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                          {subServicesExpanded.has(service.id) && (
                            <Box className="send-quotation-modal1__services-list">
                              {service.subServices.map((sub) => (
                                <Box
                                  key={sub.id}
                                  className="send-quotation-modal1__service-item"
                                >
                                  <Box className="send-quotation-modal1__service-item-row">
                                    <Typography
                                      variant="body2"
                                      className="send-quotation-modal1__service-name1"
                                    >
                                      {sub.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      className="send-quotation-modal1__service-price1"
                                    >
                                      {formatPrice(sub.price)}
                                    </Typography>
                                  </Box>
                                  {sub.description && (
                                    <Typography
                                      variant="body2"
                                      className="send-quotation-modal1__service-description1"
                                    >
                                      {sub.description}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                      {service.merchandises?.length > 0 && (
                        <Box className="send-quotation-modal1__category-item">
                          <Typography
                            variant="subtitle2"
                            className="send-quotation-modal1__category-title"
                          >
                            Merchandises
                          </Typography>
                          <Box className="send-quotation-modal1__services-list">
                            {service.merchandises.map((m) => (
                              <Box
                                key={m.id}
                                className="send-quotation-modal1__service-item"
                              >
                                <Box className="send-quotation-modal1__service-item-row">
                                  <Typography
                                    variant="body2"
                                    className="send-quotation-modal1__service-name"
                                  >
                                    {m.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    className="send-quotation-modal1__service-price"
                                  >
                                    {formatPrice(m.price)}
                                  </Typography>
                                </Box>
                                {m.description && (
                                  <Typography
                                    variant="body2"
                                    className="send-quotation-modal1__service-description"
                                  >
                                    {m.description}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>

          <Box className="send-quotation-modal1__footer">
            <Typography
              variant="caption"
              className="send-quotation-modal1__footer-note"
            >
              Quotation will be sent email summary
            </Typography>
            <Box className="send-quotation-modal1__footer-actions">
              <Button
                variant="outlined"
                onClick={onClose}
                className="send-quotation-modal1__cancel-btn"
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendQuotation}
                className="send-quotation-modal1__send-btn"
                disabled={sending || selectedServiceIds.size === 0}
              >
                Send Quotation
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default SendQuotationModal;
